<template>
  <router-link
    class="nav-item nav-dropdown open"
    :to="routeObject"
    @click.native="toggleDropdown">
    <div
      class="nav-link nav-dropdown-toggle"
      :class="[active ? 'active' : '']">
      <template v-if="icon === 'ticket'">
        <coozzy-ticket-icon
          :name="name"
          :active="active" />
      </template>
      <template v-else-if="icon === 'briefButler'">
        <coozzy-brief-butler-icon />
      </template>
      <template v-else-if="icon === 'handover'">
        <coozzy-handover-icon
          :name="name"
          :active="!active"
          class="nav-icon" />
      </template>
      <template v-else-if="icon === 'deepSign'">
        <coozzy-deep-sign-icon
          :name="name"
          :active="!active"
          class="nav-icon" />
      </template>
      <template v-else-if="icon !== 'analytics'">
        <font-awesome-icon
          :id="name"
          :icon="icon"
          class="nav-icon fa" />
      </template>
      <template v-else>
        <coozzy-analytics-icon
          :name="name"
          :active="active" />
      </template>
      <span class="link-name">{{ name }} </span>
      <!-- Dropdown indicator -->
      <span
        v-if="hasChildren && isMobileView"
        class="dropdown-indicator"
        :class="{ open: isOpen }"
        tabindex="0"
        @keydown.enter="toggleDropdown"
        @keydown.space="toggleDropdown">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16">
          <rect
            x="2"
            y="6"
            width="12"
            height="4"
            rx="2"
            fill="#fff" />
          <path
            d="M5 7l3 3 3-3"
            :transform="isOpen ? 'rotate(360 8 8)' : ''"
            fill="none"
            stroke="#00445F"
            stroke-width="2" />
        </svg>
      </span>
      <coozzy-badge
        v-if="badge !== ''"
        class="p-1"
        :design="badge === 0 || badge === '0' || badge === '-' ? 'white' : 'white-red'">
        {{ badge }}
      </coozzy-badge>
      <span
        v-if="badge !== '' && badge > 0"
        class="badge-dot" />
    </div>
    <b-tooltip
      fallback-placement="clockwise"
      :target="name"
      :custom-class="getTooltipClass()"
      placement="right">
      {{ name }}
    </b-tooltip>
    <ul
      v-show="(isOpen && hasChildren && isMobileView) || !isMobileView"
      class="nav-dropdown-items">
      <slot />
    </ul>
  </router-link>
</template>

<script>
import { logout } from '@/mixins/logout'
import CoozzyBadge from '../../badge/CoozzyBadge'
import CoozzyAnalyticsIcon from '@/framework/components/icons/CoozzyAnalyticsIcon'
import CoozzyTicketIcon from '@/framework/components/icons/CoozzyTicketIcon'
import CoozzyBriefButlerIcon from '@/framework/components/icons/CoozzyBriefButlerIcon'
import CoozzyDeepSignIcon from '@/framework/components/icons/CoozzyDeepSignIcon.vue'
import CoozzyHandoverIcon from '@/framework/components/icons/CoozzyHandoverIcon.vue'
import { responsivity } from '@/mixins/responsivity'

export default {
  name: 'CoozzySidebarNavDropdown',
  components: {
    CoozzyHandoverIcon,
    CoozzyDeepSignIcon,
    CoozzyAnalyticsIcon,
    CoozzyTicketIcon,
    CoozzyBadge,
    CoozzyBriefButlerIcon
  },
  mixins: [logout, responsivity],
  props: {
    name: {
      type: String,
      default: ''
    },
    routeName: {
      type: String,
      default: ''
    },
    routeParam: {
      type: Object,
      default: null
    },
    url: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: ''
    },
    active: {
      type: Boolean,
      default: false
    },
    badge: {
      type: [String, Number],
      default: ''
    },
    alwaysOpen: { type: Boolean, default: false } // for single links, not dropdowns
  },
  data() {
    return {
      isOpen: false
    }
  },
  computed: {
    isMobileView() {
      return this.mobileView || this.tabletView
    },
    hasChildren() {
      // Check if slot has content
      return !!this.$slots.default
    },
    routeObject() {
      if (this.routeName) {
        if (this.routeParam) {
          return {
            name: this.routeName,
            params: this.routeParam
          }
        }
        return { name: this.routeName }
      } else {
        return this.url
      }
    }
  },
  watch: {
    // Optionally, close dropdown on route change
    $route(to, from) {
      // Close only when navigating to a different path, not on query-only updates
      if (to && from && to.path !== from.path) {
        this.isOpen = false
      }
    }
  },
  methods: {
    toggleDropdown(e) {
      if (this.hasChildren) {
        e && e.preventDefault()
        this.isOpen = !this.isOpen
        // call handleNavigation only if user click on the arrow
        if (this.isMobileView && this.hasChildren && !(e.target.closest('.dropdown-indicator' || e.target.classList.contains('dropdown-indicator') || e.target.closest('.nav-icon') || e.target.classList.contains('nav-icon')))) {
          this.handleNavigation()
        }
      } else {
        this.handleNavigation()
      }
    },
    handleNavigation() {
      this.$emit('navigate', 'dropdown')
    },
    getTooltipClass() {
      switch (this.name) {
        case this.$t('message.navigation.networkPartner'):
          return 'righter-tooltip-network'
        case this.$t('message.generic.submitApplicationForm'):
          return 'righter-tooltip-applicationForm'
        case this.$t('message.navigation.dashboard.title'):
          return 'righter-tooltip2'
        case this.$t('message.navigation.realEstate'):
          return 'righter-tooltip3'
        case this.$t('message.navigation.tickets'):
          return 'righter-tooltip4'
        case this.$t('message.navigation.reminder'):
          return 'righter-tooltip5'
        case this.$t('message.navigation.calendar.title'):
          return 'righter-tooltip6'
        case this.$t('message.navigation.directory'):
          return 'righter-tooltip7'
        case this.$t('message.navigation.settings.title'):
          return 'righter-tooltip8'
        default:
          return 'righter-tooltip'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
a:hover {
  text-decoration: none !important;
}

:deep(.badge) {
  margin-right: 0 !important;
}

.icon-container {
  margin-top: -9px;
  display: inline-block;
  vertical-align: middle;
}

.nav-dropdown {
  background: transparent !important;

  ::before {
    display: none !important;
  }

  .nav-link {
    cursor: pointer;
    color: #fff;
    background: transparent !important;
    font-size: 16px;
    line-height: 24px;

    .nav-icon {
      color: inherit !important;
    }

    .icon {
      width: 20px;
      margin-right: 5px;

      &.icon-grey {
        display: none;
      }

      &.icon-white {
        display: inline;
      }
    }

    &.active {
      .icon-grey {
        display: inline;
      }

      .icon-white {
        display: none !important;
      }

      background-color: #fff !important;
      color: $color-grey-dark !important;
    }
  }
}

@media (min-width: 992px) {
  .sidebar-fixed {
    .link-name {
      display: none !important;
    }

    .badge-dot {
      display: inline-block !important;
    }
  }
  .sidebar-fixed.sidebar-lg-show {
    .link-name {
      display: inline !important;
    }

    .badge-dot {
      display: none !important;
    }
  }
}

@media (max-width: 991px) {
  .sidebar-fixed:not(.sidebar-show) {
    .link-name {
      display: none !important;
    }

    .badge-dot {
      display: inline !important;
    }
  }
  .sidebar-fixed.sidebar-lg-show {
    .link-name {
      display: inline;
    }

    .badge-dot {
      display: none !important;
    }
  }
}

.sidebar .nav-dropdown.open > .nav-dropdown-items {
  max-width: 200px;
}

.badge-dot {
  display: none;
  position: absolute;
  top: 8px;
  left: 33px;
  padding: 4px;
  border-radius: 50%;
  background: red;
  color: white;
}
.nav-dropdown-toggle {
  max-width: 200px;
}
.nav-dropdown {
  background: transparent !important;
  position: relative;
  .dropdown-indicator {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 8px;
    width: 15px;
    margin-top: 2px;
    height: 20px;
    background: #fff;
    border-radius: 4px;
    float: right;
    transition: transform 0.2s;
    svg {
      transition: transform 0.2s;
      transform: rotate(0deg);
    }
    &.open svg {
      transform: rotate(180deg);
    }
  }
  .nav-dropdown-items {
    background: transparent;
    transition: max-height 0.2s;
  }
}
.dropdown-fade-enter-active, .dropdown-fade-leave-active {
  transition: opacity 0.2s;
}
.dropdown-fade-enter, .dropdown-fade-leave-to {
  opacity: 0;
}
</style>
